import React, { ReactElement, useEffect, useState } from 'react'
import { RADOM_COLORS, formatSubscriptionDuration } from '../util/Constants'
import RadomLogo from '../icons/Logo2'
import { IconButton, SecondaryButton } from './Button'
import styled from 'styled-components'
import { InputLabel, TextInput } from './Input'
import Arrow from '../icons/Arrow'
import Close from '../icons/Close'
import { shortAddress, pickTextColorBasedOnBgColor, resolveNavigationUrl } from '../util/Util'
import Radom, {
  CheckoutItemData,
  IDiscountCode,
  IManagedGateway,
  IManagedPaymentMethodDetails,
  IManagedPaymentStatus,
  IManagedTokenDelegatePaymentMethodDetails,
  InputFieldWithValue,
  OpenBankingGateway,
  PriceQuote,
  ProductWithQuantity,
  Subscription
} from '../api/Radom'
import PayWidget from './PayWidget'
import { getTotalSum } from '../util/Products'
import { Token } from '../util/Tokens'
import Spinner from './Spinner'
import {
  Currencies,
  formatCurrency,
  TokenCurrencies
} from '../util/Currencies'
import { isEqual } from 'lodash'
import { ManagedPaymentMethod } from '../util/Managed'
import { FadeIn } from './Animations'

export const darkenColor = (color: string, amount: number = 0.2): string => {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const hexToRgb = (hex: string) => {
    hex = hex.replace('#', '')
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((c) => c + c)
        .join('')
    }
    const bigint = parseInt(hex, 16)
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255
    }
  }

  let r = 0
  let g = 0
  let b = 0

  if (color.startsWith('#')) {
    ;({ r, g, b } = hexToRgb(color))
  } else if (color.startsWith('rgb')) {
    const nums = color.match(/\d+/g)
    if (nums) {
      r = parseInt(nums[0], 10)
      g = parseInt(nums[1], 10)
      b = parseInt(nums[2], 10)
    }
  } else {
    return color // fallback
  }

  // darken
  r = Math.max(0, Math.min(255, Math.floor(r * (1 - amount))))
  g = Math.max(0, Math.min(255, Math.floor(g * (1 - amount))))
  b = Math.max(0, Math.min(255, Math.floor(b * (1 - amount))))

  return `rgba(${r}, ${g}, ${b}, 0.4)` // 0.2 alpha，或你想要的透明度
}

const getFiatCurrencySymbol = (currency: string): string => {
  try {
    const symbol = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol'
    }).formatToParts(0).find(part => part.type === 'currency')?.value

    return symbol ?? currency
  } catch {
    return currency
  }
}

const sanitizeFiatAmountInput = (rawValue: string): string => {
  const valueWithoutChars = rawValue.replace(/[^\d.]/g, '')
  const [whole = '', ...fractionParts] = valueWithoutChars.split('.')
  const fraction = fractionParts.join('').slice(0, 2)
  return fractionParts.length > 0 ? `${whole}.${fraction}` : whole
}

const normalizeFiatAmount = (rawValue: string): string => {
  if (!rawValue) {
    return ''
  }

  const parsed = Number.parseFloat(rawValue)
  if (!Number.isFinite(parsed) || parsed < 0) {
    return ''
  }

  return parsed.toFixed(2)
}

export const FadeInWrapper = styled.div`
  animation: ${FadeIn} 0.5s ease;
  font-size: 14px;
  min-width: 100%;
  background: white;
  min-height: 100vh;
  height: 100vh;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  overflow: hidden;

  @media (max-width: 900px) {
    height: auto;
    overflow: visible;
    min-height: 100vh;
  }
`
export const LeftSection = styled.div<{
  background: string
  textColor: string
  slantedEdge?: boolean
  center?: boolean
  hideOnMobile?: boolean
}>`
  background: ${({ background }) => background};
  color: ${({ textColor }) => textColor};
  display: flex;
  flex-direction: column;
  align-items: ${({ center = true }) => (center ? 'center' : 'flex-start')};
  justify-content: ${({ center = true }) => (center ? 'center' : 'flex-start')};
  padding: 0;
  width: 48%;
  position: relative;
  min-height: 100vh;
  height: 100vh;
  box-sizing: border-box;
  border-right: 1px solid rgba(15, 23, 42, 0.1);
  overflow: hidden;
  ${({ slantedEdge }) =>
    slantedEdge && 'clip-path: polygon(0 0, 100% 0, 90% 100%, 0% 100%);'}
  ${({ slantedEdge }) => slantedEdge && 'padding-right: 70px;'}

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0) 30%);
  }

  @media (max-width: 900px) {
    display: ${({ hideOnMobile = false }) => hideOnMobile ? 'none' : 'flex'};
    width: 100%;
    clip-path: none;
    border-right: none;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    min-height: auto;
    height: auto;
    align-items: flex-start;
    justify-content: flex-start;
    overflow: initial;

    &::after {
      display: none;
    }
  }
`

export const RightSection = styled.div<{ fillViewportOnMobile?: boolean }>`
  background-color: #ffffff;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  margin: 0;
  flex-direction: column;
  gap: 10px;
  flex-grow: 1;
  text-overflow: ellipsis;
  min-height: 0;
  height: 100vh;
  width: 52%;
  overflow-y: auto;
  box-sizing: border-box;
  position: relative;
  z-index: 1;
  box-shadow:
    -34px 0 64px -36px rgba(15, 23, 42, 0.34),
    -10px 0 22px -18px rgba(15, 23, 42, 0.18);
  padding:
    clamp(42px, 5.5vh, 60px)
    clamp(96px, 10.5vw, 144px)
    24px
    clamp(40px, 4.5vw, 64px);

  @media (max-width: 1320px) and (min-width: 901px) {
    padding:
      clamp(36px, 4.5vh, 52px)
      clamp(54px, 5.8vw, 82px)
      24px
      clamp(28px, 2.8vw, 40px);
  }

  @media (max-width: 900px) {
    padding: 20px 18px 24px;
    width: 100%;
    min-height: ${({ fillViewportOnMobile = false }) => fillViewportOnMobile ? '100vh' : 'auto'};
    height: auto;
    overflow: visible;
    justify-content: initial;
    align-items: stretch;
    box-shadow: none;
  }
`

const DESKTOP_MANAGED_FORM_WIDTH = '512px'
const DESKTOP_STANDARD_FORM_WIDTH = '492px'
const DESKTOP_NARROW_MANAGED_FORM_WIDTH = '500px'
const DESKTOP_NARROW_STANDARD_FORM_WIDTH = '484px'
const DESKTOP_WIDE_MANAGED_FORM_WIDTH = '528px'
const DESKTOP_WIDE_STANDARD_FORM_WIDTH = '540px'
const DESKTOP_LEFT_RAIL_MANAGED_WIDTH = DESKTOP_MANAGED_FORM_WIDTH
const DESKTOP_LEFT_RAIL_STANDARD_WIDTH = DESKTOP_STANDARD_FORM_WIDTH
const DESKTOP_LEFT_RAIL_NARROW_MANAGED_WIDTH = DESKTOP_NARROW_MANAGED_FORM_WIDTH
const DESKTOP_LEFT_RAIL_NARROW_STANDARD_WIDTH = DESKTOP_NARROW_STANDARD_FORM_WIDTH
const DESKTOP_LEFT_RAIL_WIDE_MANAGED_WIDTH = DESKTOP_WIDE_MANAGED_FORM_WIDTH
const DESKTOP_LEFT_RAIL_WIDE_STANDARD_WIDTH = DESKTOP_WIDE_STANDARD_FORM_WIDTH

const RightFormContainer = styled.div<{ isManagedFlow?: boolean }>`
  width: ${({ isManagedFlow }) => isManagedFlow ? `min(${DESKTOP_MANAGED_FORM_WIDTH}, 100%)` : `min(${DESKTOP_STANDARD_FORM_WIDTH}, 100%)`};
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  box-sizing: border-box;
  margin: clamp(26px, 4.5vh, 40px) 0 0 clamp(2px, 0.4vw, 8px);
  align-self: flex-start;

  @media (max-width: 900px) {
    width: min(100%, 620px);
    max-width: 100%;
    border: none;
    box-shadow: none;
    border-radius: 0;
    padding: 0;
    background: transparent;
    margin: 0 auto;
    align-self: center;
  }

  @media (max-width: 1320px) and (min-width: 901px) {
    width: ${({ isManagedFlow }) => isManagedFlow ? `min(${DESKTOP_NARROW_MANAGED_FORM_WIDTH}, 100%)` : `min(${DESKTOP_NARROW_STANDARD_FORM_WIDTH}, 100%)`};
    margin-left: 0;
  }

  @media (min-width: 1600px) {
    width: ${({ isManagedFlow }) => isManagedFlow ? `min(${DESKTOP_WIDE_MANAGED_FORM_WIDTH}, 100%)` : `min(${DESKTOP_WIDE_STANDARD_FORM_WIDTH}, 100%)`};
  }
`
export const PayWidgetFitForPhone = styled.div`
  width: 100%;
  min-height: min-content;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  margin: 0;
  @media (max-width: 900px) {
    width: 100%;
    height: auto;
  }
`

const LeftSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding:
    26px
    clamp(34px, 4vw, 56px)
    28px
    clamp(40px, 4.5vw, 56px);
  width: 100%;
  max-width: 700px;
  margin: 0;
  flex: 1;
  min-height: 0;

  @media (max-width: 900px) {
    width: 100%;
    padding: 18px 20px 18px;
    gap: 16px;
  }
`

const LEFT_RAIL_COLUMN_OFFSET = 'clamp(76px, 7vw, 124px)'
const LEFT_RAIL_COLUMN_GUTTER = 'clamp(8px, 1vw, 14px)'

interface LeftRailColumnProps {
  $columnWidth: string
  $columnWidthNarrow: string
  $columnWidthWide: string
}

const InputAmountBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 32px;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
  margin-top: 0;

  @media (max-width: 900px) {
    margin-top: 0;
    width: 100%;
  }
`

const AmountInputWrapper = styled.div`
  width: 100%;
  position: relative;
`

const AmountInputSymbol = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #667085;
  font-size: 20px;
  font-weight: 500;
  pointer-events: none;
`

const AmountInput = styled(TextInput)`
  height: 52px;
  padding-left: 34px;
  font-size: 26px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.2px;
`

const CurrencyPill = styled.button<{ selected: boolean }>`
  border: 1px solid;
  border-color: ${({ selected }) => selected ? RADOM_COLORS.BLUE5 : RADOM_COLORS.GRAY_DARK};
  background: ${({ selected }) => selected ? RADOM_COLORS.BLUE7 : 'white'};
  color: ${RADOM_COLORS.BLACK};
  border-radius: 8px;
  height: 34px;
  min-width: 56px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: 0.2s ease all;

  :hover {
    border-color: ${({ selected }) => selected ? RADOM_COLORS.BLUE5 : '#c4ccde'};
  }

  :focus-visible {
    border-color: ${RADOM_COLORS.BLUE5};
    box-shadow: 0 0 0 3px rgb(97 138 250 / 18%);
    outline: none;
  }
`

const SummaryCard = styled.div`
  width: 100%;
  background: transparent;
`

const SummaryCardHeader = styled.div<LeftRailColumnProps>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: ${({ $columnWidth }) => $columnWidth};
  margin-left: ${LEFT_RAIL_COLUMN_OFFSET};
  padding-bottom: 18px;
  border-bottom: 1px solid #dbe1ec;

  @media (max-width: 1320px) and (min-width: 901px) {
    max-width: ${({ $columnWidthNarrow }) => $columnWidthNarrow};
  }

  @media (min-width: 1600px) {
    max-width: ${({ $columnWidthWide }) => $columnWidthWide};
  }

  @media (max-width: 900px) {
    max-width: 100%;
    margin-left: 0;
    padding-bottom: 14px;
  }
`

const SummaryCardBody = styled.div<LeftRailColumnProps>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: ${({ $columnWidth }) => $columnWidth};
  padding: clamp(88px, 10vh, 126px) 0 0;
  margin-left: ${LEFT_RAIL_COLUMN_OFFSET};

  @media (max-width: 1320px) and (min-width: 901px) {
    max-width: ${({ $columnWidthNarrow }) => $columnWidthNarrow};
  }

  @media (min-width: 1600px) {
    max-width: ${({ $columnWidthWide }) => $columnWidthWide};
    margin-left: clamp(76px, 6vw, 124px);
  }

  @media (max-width: 900px) {
    max-width: 100%;
    margin-left: 0;
    padding-top: 24px;
  }
`

const LeftFooter = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin-top: auto;
  padding:
    0
    clamp(34px, 4vw, 56px)
    22px
    clamp(40px, 4.5vw, 56px);
  font-size: 13px;
  display: flex;
  justify-content: flex-start;

  @media (max-width: 900px) {
    padding: 4px 20px 18px;
  }
`

const LeftFooterInline = styled.div<LeftRailColumnProps>`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
  white-space: nowrap;
  min-width: max-content;
  width: 100%;
  max-width: ${({ $columnWidth }) => $columnWidth};
  margin-left: ${LEFT_RAIL_COLUMN_OFFSET};

  @media (max-width: 1320px) and (min-width: 901px) {
    max-width: ${({ $columnWidthNarrow }) => $columnWidthNarrow};
  }

  @media (min-width: 1600px) {
    max-width: ${({ $columnWidthWide }) => $columnWidthWide};
  }

  @media (max-width: 900px) {
    max-width: 100%;
    margin-left: 0;
    width: auto;
    min-width: 0;
  }
`

const MobileRightFooter = styled.div`
  display: none;

  @media (max-width: 900px) {
    display: flex;
    justify-content: flex-start;
    margin-top: auto;
    padding: 28px 2px 8px;
  }
`

export interface CheckoutCustomizations {
  leftPanelColor?: string
  slantedEdge?: boolean
  primaryButtonColor?: string
  allowDiscountCodes?: boolean
  displayQrCode?: boolean
  allowFreeInputAmount: boolean
}

export interface CheckoutData {
  id: string
  organizationId?: string
  products?: ProductWithQuantity[]
  lineItems?: CheckoutItemData[]
  subscription?: Subscription
  gateway: {
    managed?: IManagedGateway
    openBanking?: OpenBankingGateway
  }
  sellerLogoUrl: string
  sellerName: string
  sellerAddress: string
  customizations?: CheckoutCustomizations
  isInvoice?: boolean
  currency?: string
  total?: number
  successUrl?: string
  sessionStatus?: string
  cancelUrl?: string
  inputFields?: InputFieldWithValue[]
  chargeCustomerNetworkFee: boolean
  presaleData?: PresaleData
  displayQrCode?: boolean
  travelRuleFields?: Partial<TravelRuleFieldsCollected>
  travelRuleEmail?: string
  updatedAt?: string | Date
  createdAt?: string | Date
}
export interface bonusLevel {
  greaterThan: number
  lessThan: number
  bonus: number
}
export interface amountBasedPricingStrategy {
  name: string
  targetAmount: string
  priceIncreaseTo: string
}

export interface PresaleData {
  tokenPresaleData?: {
    totalRaiseAmount?: string
    totalAmountRaised?: string
    minimumPurchaseAmount?: string
    startsAt?: string
    endsAt?: string
    bonus?: bonusLevel[]
    pricingStrategies?: {
      timeBased?: {
        startsAt: string
        roundDurationSeconds: number
        priceIncreasePercentage: number
      }
      amountBased?: amountBasedPricingStrategy[]
      goalBased?: any
    }
    customizations?: {
      title?: string
      bonusTitle?: string
      titleFontSize?: string
      titleFontColor?: string
      titleFontFamily?: string
      hideTotalRaiseGoal?: boolean
    }
  }
}

interface IProps {
  checkoutData: CheckoutData
  onCheckout: (params: {
    selfCustodial?: {
      useRadomBalance: boolean
      selectedChainId: number
      selectedToken: Token
    }
    managed?: {
      selectedMethod: ManagedPaymentMethod
    }
    priceQuotes: PriceQuote[]
  }) => void
  isSuccess: boolean
  managedPaymentDetails?: IManagedPaymentMethodDetails
  tokenDelegatePaymentDetails?: IManagedTokenDelegatePaymentMethodDetails
  successElement: ReactElement
  isPreProcessingPayment: boolean
  errorMessage?: string
  onDiscountCodeEntered?: (discountCode: string) => void
  onCurrency?: (currency: string) => void
  resetDiscountCode?: () => void
  discountResult?: {
    success: boolean
    element: ReactElement
  }
  discount?: IDiscountCode
  discountLocked?: boolean
  onInputFieldsChange?: (inputs: InputFieldWithValue[]) => void
  paymentStatus?: IManagedPaymentStatus
  onChangePaymentMethod?: () => void
  hideTravelRuleFields?: boolean
  travelRuleFieldsCollected: TravelRuleFieldsCollected
  onTotalChange?: (total: number) => void
}

// ---------------------------------------------------------------------------------------------------------------------
export interface TravelRuleFields {
  accountType?: string
  companyName?: string
  name?: string
  emailAddress?: string
  addressLine1?: string
  addressLine2?: string
  townOrCity?: string
  postalCode?: string
  stateOrProvince?: string
  countryOfResidence?: string
}

export function emptyTravelRuleFields(): TravelRuleFields {
  return {
    accountType: '',
    companyName: '',
    name: '',
    emailAddress: '',
    addressLine1: '',
    addressLine2: '',
    townOrCity: '',
    postalCode: '',
    stateOrProvince: '',
    countryOfResidence: ''
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// Travel rule field collection flags
export interface TravelRuleFieldsCollected {
  accountType?: string
  companyNameCollected: boolean
  nameCollected: boolean
  emailAddressCollected: boolean
  addressLine1Collected: boolean
  addressLine2Collected: boolean
  townOrCityCollected: boolean
  postalCodeCollected: boolean
  stateOrProvinceCollected: boolean
  countryOfResidenceCollected: boolean
  dateOfBirthCollected?: boolean
}

export function zeroTravelRuleFieldsCollected(): TravelRuleFieldsCollected {
  return {
    accountType: undefined,
    companyNameCollected: false,
    nameCollected: false,
    emailAddressCollected: false,
    addressLine1Collected: false,
    addressLine2Collected: false,
    townOrCityCollected: false,
    postalCodeCollected: false,
    stateOrProvinceCollected: false,
    countryOfResidenceCollected: false,
    dateOfBirthCollected: false
  }
}

export function allTravelRuleFieldsCollected(): TravelRuleFieldsCollected {
  return {
    accountType: CheckoutType.Individual,
    companyNameCollected: true,
    nameCollected: true,
    emailAddressCollected: true,
    addressLine1Collected: true,
    addressLine2Collected: true,
    townOrCityCollected: true,
    postalCodeCollected: true,
    stateOrProvinceCollected: true,
    countryOfResidenceCollected: true,
    dateOfBirthCollected: true
  }
}

export enum CheckoutType {
  Individual = 'Individual',
  Business = 'Business'
}

// ---------------------------------------------------------------------------------------------------------------------
function Checkout(props: IProps): ReactElement {
  const { checkoutData, onCheckout } = props

  const [localCheckoutData, setLocalCheckoutData] = useState({} as any)
  const [isLoading, setIsLoading] = useState(true)
  const [showDiscountCodeInput, setShowDiscountCodeInput] = useState(false)
  const [totalSum, setTotalSum] = useState(0)
  const [subscriptionSum, setSubscriptionSum] = useState(0)
  const [currency, setCurrency] = useState('USD')
  const [discountCode, setDiscountCode] = useState('')
  const [payAmount, setPayAmount] = useState('')
  const [formatPayAmount, setFormatPayAmount] = useState('')
  const [payCurrency, setPayCurrency] = useState(
    props.checkoutData.products?.[0]?.product?.currency ??
    props.checkoutData.lineItems?.[0]?.currency ??
    props.checkoutData.currency ??
    'USD'
  )
  const [controlFreeInput, setControlFreeInput] = useState(false)
  const [lockedPayAmount, setLockedPayAmount] = useState('')
  const [lockedPayCurrency, setLockedPayCurrency] = useState('')
  const [hideMobileSummary, setHideMobileSummary] = useState(false)

  const subscriptionProducts =
    checkoutData.products?.filter(
      (p) => p.product.chargingIntervalSeconds > 0
    ) ?? []
  const firstProduct =
    subscriptionProducts.length > 0
      ? subscriptionProducts?.[0]
      : checkoutData?.products?.[0] ?? undefined

  const paymentLinkCustomizations = checkoutData.customizations
  const customizations = {
    leftPanelColor:
      paymentLinkCustomizations?.leftPanelColor ?? '#eff2f8',
    primaryButtonColor:
      paymentLinkCustomizations?.primaryButtonColor ?? RADOM_COLORS.ORANGE,
    slantedEdge: paymentLinkCustomizations?.slantedEdge ?? false,
    allowDiscountCodes: paymentLinkCustomizations?.allowDiscountCodes ?? false
  }
  const merchantAccentColor = customizations.primaryButtonColor ?? '#3b65f0'

  const leftTextColor = pickTextColorBasedOnBgColor(
    customizations.leftPanelColor,
    'white',
    'black'
  )
  const leftMutedTextColor = pickTextColorBasedOnBgColor(
    customizations.leftPanelColor,
    'rgba(255, 255, 255, 0.76)',
    'rgba(31, 41, 55, 0.68)'
  )
  const leftFooterTextColor = pickTextColorBasedOnBgColor(
    customizations.leftPanelColor,
    'rgba(255, 255, 255, 0.64)',
    'rgba(55, 65, 81, 0.72)'
  )
  const leftSubtleBorderColor = pickTextColorBasedOnBgColor(
    customizations.leftPanelColor,
    'rgba(255, 255, 255, 0.2)',
    'rgba(15, 23, 42, 0.14)'
  )
  const sellerDisplayName =
    checkoutData.sellerName && checkoutData.sellerName.length > 0
      ? checkoutData.sellerName
      : (shortAddress(checkoutData.sellerAddress, 8) || 'Merchant')
  const sellerDisplayInitial = sellerDisplayName.trim().charAt(0).toUpperCase() || 'M'
  const merchantDescriptionSource = (
    checkoutData.subscription?.product?.description ??
    checkoutData.products?.find((line) => (line.product?.description?.trim()?.length ?? 0) > 0)?.product.description ??
    checkoutData.lineItems?.find((item) => (item.description?.trim()?.length ?? 0) > 0)?.description
  )?.trim()
  const merchantDescription = merchantDescriptionSource && merchantDescriptionSource.length > 90
    ? `${merchantDescriptionSource.slice(0, 90)}...`
    : merchantDescriptionSource
  const hasProductSummaryRows = (checkoutData.products?.length ?? 0) > 0
  const hasLineItemSummaryRows = (checkoutData.lineItems?.length ?? 0) > 0
  const showEmptyOrderFallback =
    !checkoutData.customizations?.allowFreeInputAmount &&
    subscriptionProducts.length === 0 &&
    !hasProductSummaryRows &&
    !hasLineItemSummaryRows
  const isManagedFlow = !!checkoutData.gateway.managed
  const leftRailColumnWidth = `min(${isManagedFlow ? DESKTOP_LEFT_RAIL_MANAGED_WIDTH : DESKTOP_LEFT_RAIL_STANDARD_WIDTH}, calc(100% - ${LEFT_RAIL_COLUMN_OFFSET} - ${LEFT_RAIL_COLUMN_GUTTER}))`
  const leftRailColumnWidthNarrow = `min(${isManagedFlow ? DESKTOP_LEFT_RAIL_NARROW_MANAGED_WIDTH : DESKTOP_LEFT_RAIL_NARROW_STANDARD_WIDTH}, calc(100% - ${LEFT_RAIL_COLUMN_OFFSET} - ${LEFT_RAIL_COLUMN_GUTTER}))`
  const leftRailColumnWidthWide = `min(${isManagedFlow ? DESKTOP_LEFT_RAIL_WIDE_MANAGED_WIDTH : DESKTOP_LEFT_RAIL_WIDE_STANDARD_WIDTH}, calc(100% - 176px))`
  const handleBack = (): void => {
    if (checkoutData.cancelUrl && checkoutData.cancelUrl.length > 0) {
      window.location.href = resolveNavigationUrl(checkoutData.cancelUrl)
      return
    }

    if (window.history.length > 1) {
      window.history.back()
    }
  }

  const getPriceQuotes = (): void => {
    const normalizedCheckoutTotal = Number(checkoutData.total ?? 0)
    const hasProvidedTotal =
      Number.isFinite(normalizedCheckoutTotal) && normalizedCheckoutTotal > 0

    let nextCurrency = checkoutData.currency ?? 'USD'
    if (subscriptionProducts.length > 0) {
      nextCurrency = subscriptionProducts[0]?.product.currency ?? 'USD'
    } else if (checkoutData.products && checkoutData.products.length > 0) {
      nextCurrency = checkoutData.products?.[0]?.product?.currency ?? 'USD'
    } else if (checkoutData.lineItems && checkoutData.lineItems.length > 0) {
      nextCurrency = checkoutData.lineItems?.[0]?.currency ?? checkoutData.currency ?? 'USD'
    } else {
      if (!checkoutData.customizations?.allowFreeInputAmount) {
        if (hasProvidedTotal) {
          setCurrency(checkoutData.currency ?? nextCurrency)
          setTotalSum(normalizedCheckoutTotal)
          setSubscriptionSum(0)
        }
        setIsLoading(false)
        return
      }
    }

    setCurrency(nextCurrency)

    const fromCurrencies = checkoutData.customizations?.allowFreeInputAmount
      ? [payCurrency]
      : [
        ...(checkoutData.products?.map((p) => p.product.currency) ?? []),
        ...(checkoutData.lineItems?.map((lineItem) => lineItem.currency ?? nextCurrency) ?? []),
        ...(checkoutData.currency ? [checkoutData.currency] : [])
      ]

    const uniqueFromCurrencies = [
      ...new Set(
        fromCurrencies
          .filter((value): value is string => typeof value === 'string' && value.length > 0)
          .map((value) => value.toUpperCase())
      )
    ]

    if (uniqueFromCurrencies.length === 0) {
      if (hasProvidedTotal) {
        setTotalSum(normalizedCheckoutTotal)
        setSubscriptionSum(0)
      }
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    Radom.getPriceQuotes(uniqueFromCurrencies, [nextCurrency])
      .then((p) => {
        // Get price quotes in terms of first product's currency
        const [total, subscriptionTotal] = getTotalSum(
          checkoutData,
          p,
          nextCurrency
        )
        const normalizedTotal = Number(total)
        const normalizedSubscriptionTotal = Number(subscriptionTotal)

        setTotalSum(
          Number.isFinite(normalizedTotal) && normalizedTotal > 0
            ? normalizedTotal
            : hasProvidedTotal
              ? normalizedCheckoutTotal
              : 0
        )
        setSubscriptionSum(
          Number.isFinite(normalizedSubscriptionTotal)
            ? normalizedSubscriptionTotal
            : 0
        )
      })
      .catch((err) => {
        console.error('Failed to load checkout price quotes', err)
        if (hasProvidedTotal) {
          setTotalSum(normalizedCheckoutTotal)
          setSubscriptionSum(0)
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    getPriceQuotes()
  }, [])

  const effectivePayCurrency = controlFreeInput && lockedPayCurrency.length > 0
    ? lockedPayCurrency
    : payCurrency
  const effectivePayAmount = controlFreeInput && lockedPayAmount.length > 0
    ? lockedPayAmount
    : payAmount

  const lockFreeInputSelection = (): CheckoutParams['freeInput'] | undefined => {
    if (!checkoutData.customizations?.allowFreeInputAmount) {
      return undefined
    }

    const normalizedAmount = controlFreeInput && lockedPayAmount.length > 0
      ? lockedPayAmount
      : normalizeFiatAmount(payAmount || formatPayAmount)
    const amount = normalizedAmount ? parseFloat(normalizedAmount) : 0

    if (!controlFreeInput) {
      setPayAmount(normalizedAmount)
      setFormatPayAmount(normalizedAmount)
      setLockedPayAmount(normalizedAmount)
      setLockedPayCurrency(payCurrency)
      setControlFreeInput(true)
    }

    props.onCurrency?.(payCurrency)
    props.onTotalChange?.(amount)

    return {
      amount,
      currency: payCurrency
    }
  }

  useEffect(() => {
    if (
      isEqual(localCheckoutData.gateway, checkoutData.gateway) &&
      isEqual(localCheckoutData.products, checkoutData.products) &&
      isEqual(localCheckoutData.lineItems, checkoutData.lineItems) &&
      isEqual(localCheckoutData.currency, checkoutData.currency) &&
      isEqual(localCheckoutData.total, checkoutData.total)
    ) {
      return
    }

    setLocalCheckoutData(checkoutData)
    setIsLoading(true)
    getPriceQuotes()
  }, [checkoutData])

  useEffect(() => {
    if (!checkoutData.gateway.managed) {
      setHideMobileSummary(false)
    }
  }, [checkoutData.gateway.managed, checkoutData.id])

  useEffect(() => {
    if (props.discountResult?.success) {
      setShowDiscountCodeInput(true)
    }
  }, [props.discountResult])

  return (
    <FadeInWrapper>
      <LeftSection
        center={false}
        slantedEdge={customizations.slantedEdge}
        background={customizations.leftPanelColor}
        textColor={leftTextColor}
        hideOnMobile={hideMobileSummary}
      >
        <LeftSectionContent
          style={{
            maxWidth: checkoutData.customizations?.allowFreeInputAmount ? 'min(820px, 100%)' : 'min(980px, 100%)',
            gap: 20
          }}
        >
          <SummaryCard>
            <SummaryCardHeader
              $columnWidth={leftRailColumnWidth}
              $columnWidthNarrow={leftRailColumnWidthNarrow}
              $columnWidthWide={leftRailColumnWidthWide}
              style={{ borderBottomColor: leftSubtleBorderColor }}
            >
              <IconButton
                aria-label="Go back"
                onClick={handleBack}
                style={{
                  padding: 2,
                  marginRight: 4,
                  color: leftMutedTextColor,
                  flexShrink: 0
                }}
              >
                <Arrow
                  width={18}
                  height={18}
                  stroke={leftMutedTextColor}
                  style={{ transform: 'rotate(180deg)' }}
                />
              </IconButton>
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  overflow: 'hidden',
                  flexShrink: 0,
                  backgroundColor: merchantAccentColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                {checkoutData.sellerLogoUrl
                  ? <img src={checkoutData.sellerLogoUrl} style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} />
                  : <span>{sellerDisplayInitial}</span>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ fontWeight: 400, lineHeight: 1.2, fontSize: 17, color: leftTextColor }}>
                  {sellerDisplayName}
                </span>
                {merchantDescription && (
                  <span
                    style={{
                      marginTop: 2,
                      fontSize: 13,
                      color: leftMutedTextColor,
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 360
                    }}
                    title={merchantDescription}
                  >
                    {merchantDescription}
                  </span>
                )}
              </div>
            </SummaryCardHeader>
            <SummaryCardBody
              $columnWidth={leftRailColumnWidth}
              $columnWidthNarrow={leftRailColumnWidthNarrow}
              $columnWidthWide={leftRailColumnWidthWide}
            >
              {!checkoutData.customizations?.allowFreeInputAmount && subscriptionProducts.length === 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 4 }}>
                  <span style={{ color: leftMutedTextColor, fontSize: 14, fontWeight: 400 }}>
                    Pay {sellerDisplayName}
                  </span>
                  <span style={{ color: leftTextColor, fontSize: 37, lineHeight: 1, fontWeight: 500 }}>
                    {formatCurrency(currency, totalSum)}
                  </span>
                </div>
              )}
              {subscriptionProducts.length > 0 && (
                <>
                  <span
                    style={{
                      fontSize: 28,
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <span style={{ fontWeight: 400, fontSize: 16, opacity: 0.68 }}>
                  Subscribe to
                    </span>
                    {subscriptionProducts[0].product.name}
                  </span>
                  {isLoading && <Spinner />}
                  {!isLoading && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: 54,
                        fontWeight: 500
                      }}
                    >
                      {TokenCurrencies.includes(currency) && (
                        <div style={{ width: 32, marginRight: '8px' }}>
                          {Currencies.find((c) => c.ticker === currency)?.icon}
                        </div>
                      )}
                      {formatCurrency(currency, totalSum)}
                      {subscriptionProducts.length ===
                    checkoutData.products?.length && (
                        <span
                          style={{
                            fontSize: 16,
                            marginLeft: 5,
                            fontWeight: 500,
                            width: 30,
                            opacity: 0.75,
                            display: 'inline-block'
                          }}
                        >
                      per{' '}
                          {formatSubscriptionDuration(
                            subscriptionProducts[0].product.chargingIntervalSeconds
                          ).toLowerCase()}
                        </span>
                      )}
                    </div>
                  )}
                  {subscriptionProducts.length !==
                checkoutData.products?.length && (
                    <span style={{ fontSize: 18, opacity: 0.65 }}>
                  Then {formatCurrency(currency, subscriptionSum)} per{' '}
                      {formatSubscriptionDuration(
                        subscriptionProducts[0].product.chargingIntervalSeconds
                      ).toLowerCase()}
                    </span>
                  )}
                  {checkoutData.products?.length === 1 &&
                firstProduct &&
                firstProduct.product.imageUrl && (
                    <img
                      style={{
                        marginTop: 10,
                        maxWidth: 200,
                        maxHeight: 200,
                        borderRadius: 5
                      }}
                      src={firstProduct.product.imageUrl}
                    />
                  )}
                </>
              )}
              {subscriptionProducts.length === 0 &&
            !checkoutData.isInvoice &&
            (
              <>
                {checkoutData.customizations?.allowFreeInputAmount
                  ? (
                    <InputAmountBox>
                      <InputLabel
                        style={{
                          color: leftMutedTextColor,
                          fontSize: 18,
                          fontWeight: 500,
                          marginBottom: 10,
                          textAlign: 'left',
                          width: '100%'
                        }}
                      >
                      Enter Payment Amount
                      </InputLabel>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                        {['USD', 'EUR', 'GBP'].map((fiat) => (
                          <CurrencyPill
                            key={fiat}
                            type="button"
                            selected={payCurrency === fiat}
                            disabled={controlFreeInput}
                            onClick={() => {
                              if (controlFreeInput) {
                                return
                              }
                              setCurrency(fiat)
                              setPayCurrency(fiat)
                              props.onCurrency?.(fiat)
                              if (payAmount) setFormatPayAmount(payAmount)
                            }}
                          >
                            {fiat}
                          </CurrencyPill>
                        ))}
                      </div>
                      <AmountInputWrapper>
                        <AmountInputSymbol>{getFiatCurrencySymbol(payCurrency)}</AmountInputSymbol>
                        <AmountInput
                          disabled={controlFreeInput}
                          autoFocus
                          placeholder="0.00"
                          min="0"
                          autoComplete="off"
                          inputMode="decimal"
                          aria-label="Payment amount"
                          value={formatPayAmount}
                          onFocus={(e) => {
                            setFormatPayAmount(payAmount)
                            e.currentTarget.select()
                          }}
                          onBlur={() => {
                            const normalized = normalizeFiatAmount(payAmount)
                            setPayAmount(normalized)
                            setFormatPayAmount(normalized)
                            props.onCurrency?.(payCurrency)
                            props.onTotalChange?.(normalized ? parseFloat(normalized) : 0)
                          }}
                          onChange={(e) => {
                            const value = sanitizeFiatAmountInput(e.target.value)
                            setPayAmount(value)
                            setFormatPayAmount(value)
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              e.currentTarget.blur()
                            }
                          }}
                        />
                      </AmountInputWrapper>
                    </InputAmountBox>
                  )
                  : (
                    <div style={{ fontSize: 13, fontWeight: 400, color: leftMutedTextColor }}>
                    Order summary
                    </div>
                  )}
                {isLoading && <Spinner />}
              </>
            )}

              {!checkoutData.customizations?.allowFreeInputAmount &&
            subscriptionProducts.length === 0 &&
            checkoutData.products &&
            checkoutData.products.length > 0 && (
                <div style={{
                  marginTop: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  borderTop: `1px solid ${leftSubtleBorderColor}`
                }}>
                  <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                    {checkoutData.products.map((line, index) => (
                      <div
                        key={line.product.id || `${line.product.name}-${index}`}
                        style={{
                          fontSize: 14,
                          display: 'flex',
                          width: '100%',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 12,
                          padding: '12px 0',
                          borderBottom: `1px solid ${leftSubtleBorderColor}`
                        }}
                      >
                        {line.product.imageUrl &&
                      <img
                        style={{ width: 36, height: 36, borderRadius: 6, flexShrink: 0 }}
                        src={line.product.imageUrl}
                      />}
                        <div style={{ flexGrow: 1, minWidth: 0 }}>
                          <div style={{ marginBottom: 4, fontWeight: 400, overflowWrap: 'anywhere' }}>
                            {line.product.name}
                          </div>
                          <div style={{ fontSize: 12, opacity: 0.65 }}>
                        Qty {line.quantity ?? 1}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', whiteSpace: 'nowrap' }}>
                          <span style={{ fontWeight: 400 }}>
                            {formatCurrency(line.product.currency, line.product.price * (line.quantity ?? 1))}
                          </span>
                          {(line.quantity ?? 1) > 1 &&
                        <span style={{ fontSize: '12px', textAlign: 'right', color: leftMutedTextColor }}>
                          {formatCurrency(line.product.currency, line.product.price)} each
                        </span>}
                        </div>
                      </div>
                    ))}
                  </div>

                  {!isLoading && (
                    <div style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingTop: 12,
                      borderBottom: `1px solid ${leftSubtleBorderColor}`,
                      paddingBottom: 12
                    }}>
                      <span style={{ color: leftTextColor, fontWeight: 500 }}>Total due</span>
                      <span style={{ fontWeight: 500, fontSize: 17 }}>
                        {formatCurrency(currency, totalSum)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {!checkoutData.customizations?.allowFreeInputAmount &&
            subscriptionProducts.length === 0 &&
            (!checkoutData.products || checkoutData.products.length === 0) &&
            checkoutData.lineItems &&
            checkoutData.lineItems.length > 0 && (
                <div style={{
                  marginTop: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  borderTop: `1px solid ${leftSubtleBorderColor}`
                }}>
                  <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                    {checkoutData.lineItems.map((lineItem, index) => (
                      <div
                        key={`${lineItem.name}-${index}`}
                        style={{
                          fontSize: 14,
                          display: 'flex',
                          width: '100%',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 12,
                          padding: '12px 0',
                          borderBottom: `1px solid ${leftSubtleBorderColor}`
                        }}
                      >
                        {lineItem.imageUrl &&
                      <img
                        style={{ width: 36, height: 36, borderRadius: 6, flexShrink: 0 }}
                        src={lineItem.imageUrl}
                      />}
                        <div style={{ flexGrow: 1, minWidth: 0 }}>
                          <div style={{ marginBottom: 4, fontWeight: 400, overflowWrap: 'anywhere' }}>
                            {lineItem.name}
                          </div>
                          <div style={{ fontSize: 12, opacity: 0.65 }}>
                        Qty {lineItem.quantity ?? 1}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', whiteSpace: 'nowrap' }}>
                          <span style={{ fontWeight: 400 }}>
                            {formatCurrency(lineItem.currency ?? currency, lineItem.price * (lineItem.quantity ?? 1))}
                          </span>
                          {(lineItem.quantity ?? 1) > 1 &&
                        <span style={{ fontSize: '12px', textAlign: 'right', color: leftMutedTextColor }}>
                          {formatCurrency(lineItem.currency ?? currency, lineItem.price)} each
                        </span>}
                        </div>
                      </div>
                    ))}
                  </div>

                  {!isLoading && (
                    <div style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingTop: 12,
                      borderBottom: `1px solid ${leftSubtleBorderColor}`,
                      paddingBottom: 12
                    }}>
                      <span style={{ color: leftTextColor, fontWeight: 500 }}>Total due</span>
                      <span style={{ fontWeight: 500, fontSize: 17 }}>
                        {formatCurrency(currency, totalSum)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {showEmptyOrderFallback && (
                <div style={{
                  marginTop: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  borderTop: `1px solid ${leftSubtleBorderColor}`
                }}>
                  <div style={{
                    fontSize: 14,
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '12px 0',
                    borderBottom: `1px solid ${leftSubtleBorderColor}`
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ marginBottom: 0, fontWeight: 400, overflowWrap: 'anywhere' }}>
                        Payment
                      </div>
                      <div style={{ fontSize: 12, color: leftMutedTextColor }}>
                        Qty 1
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', whiteSpace: 'nowrap' }}>
                      <span>{formatCurrency(currency, totalSum)}</span>
                    </div>
                  </div>
                  {!isLoading && (
                    <div style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingTop: 12,
                      borderBottom: `1px solid ${leftSubtleBorderColor}`,
                      paddingBottom: 12
                    }}>
                      <span style={{ color: leftTextColor, fontWeight: 500 }}>Total due</span>
                      <span style={{ fontWeight: 500, fontSize: 17 }}>
                        {formatCurrency(currency, totalSum)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {customizations.allowDiscountCodes &&
            !showDiscountCodeInput &&
            !props.discountLocked && (
                <IconButton
                  onClick={() => {
                    if (props.onDiscountCodeEntered) { setShowDiscountCodeInput(true) }
                  }}
                  style={{
                    marginTop: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    padding: 0,
                    color: leftMutedTextColor
                  }}
                >
                  <span>Add discount code</span>
                  <Arrow
                    stroke={leftMutedTextColor}
                    height={15}
                  />
                </IconButton>
              )}
              {showDiscountCodeInput && (
                <div
                  style={{
                    marginTop: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    width: '100%',
                    maxWidth: 400,
                    marginLeft: 'auto',
                    marginRight: 'auto'
                  }}
                >
                  {!props.discountResult?.success && !props.discountLocked && (
                    <div
                      style={{
                        borderRadius: 5,
                        border: '1px solid',
                        backgroundColor: pickTextColorBasedOnBgColor(
                          customizations.leftPanelColor,
                          'rgba(255, 255, 255, 0.1)',
                          'rgba(255, 255, 255, 0.25)'
                        ),
                        color: pickTextColorBasedOnBgColor(
                          customizations.leftPanelColor,
                          'white',
                          'black'
                        ),
                        borderColor: pickTextColorBasedOnBgColor(
                          customizations.leftPanelColor,
                          'rgba(255, 255, 255, 0.1)',
                          'rgba(0, 0, 0, 0.1)'
                        ),
                        display: 'flex',
                        alignItems: 'center',
                        padding: 5
                      }}
                    >
                      <TextInput
                        autoFocus
                        placeholder="Enter code"
                        autoCorrect="off"
                        spellCheck="false"
                        type="text"
                        onChange={(e) => setDiscountCode(e.target.value)}
                        style={{
                          color: pickTextColorBasedOnBgColor(
                            customizations.leftPanelColor,
                            'white',
                            'black'
                          ),
                          background: 'none',
                          border: 'none',
                          boxShadow: 'none',
                          flex: 1
                        }}
                      />
                      <SecondaryButton
                        onClick={() => props.onDiscountCodeEntered?.(discountCode)}
                        style={{
                          padding: '3px 10px',
                          textTransform: 'uppercase',
                          color: pickTextColorBasedOnBgColor(
                            customizations.leftPanelColor,
                            'white',
                            'black'
                          ),
                          border: 'none'
                        }}
                      >
                    Apply
                      </SecondaryButton>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 10 }}>
                    {props.discountResult?.element}
                    {props.discountResult?.success && !props.discountLocked && (
                      <IconButton
                        style={{ padding: 5 }}
                        onClick={() => props.resetDiscountCode?.()}
                      >
                        <Close
                          width={15}
                          height={15}
                          stroke={pickTextColorBasedOnBgColor(
                            customizations.leftPanelColor,
                            'white',
                            'black'
                          )}
                          style={{ opacity: 0.5 }}
                        />
                      </IconButton>
                    )}
                  </div>
                </div>
              )}
            </SummaryCardBody>
          </SummaryCard>

        </LeftSectionContent>

        <LeftFooter style={{ color: leftFooterTextColor }}>
          <LeftFooterInline
            $columnWidth={leftRailColumnWidth}
            $columnWidthNarrow={leftRailColumnWidthNarrow}
            $columnWidthWide={leftRailColumnWidthWide}
          >
            <a
              href="https://radom.com"
              target="_blank"
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                cursor: 'pointer',
                userSelect: 'none',
                color: 'inherit',
                fontWeight: 400,
                textDecoration: 'none'
              }}
              rel="noreferrer"
            >
              <span>Powered by</span>
              <RadomLogo
                width={50}
                fill={leftFooterTextColor}
              />
            </a>
            <span style={{ opacity: 0.5 }}>|</span>
            <a
              href="https://www.radom.com/terms"
              target="_blank"
              rel="noreferrer"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
            Terms
            </a>
            <span style={{ opacity: 0.5 }}>|</span>
            <a
              href="https://radom.com/privacy"
              target="_blank"
              rel="noreferrer"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
            Privacy
            </a>
          </LeftFooterInline>
        </LeftFooter>
      </LeftSection>

      <RightSection fillViewportOnMobile={hideMobileSummary}>
        <RightFormContainer isManagedFlow={!!checkoutData.gateway.managed}>
          <PayWidgetFitForPhone>
            <PayWidget
              checkoutData={{
                ...checkoutData,
                total: effectivePayAmount ? parseFloat(effectivePayAmount) : totalSum,
                currency: effectivePayCurrency
              }}
              currency={effectivePayCurrency}
              isPreProcessingPayment={props.isPreProcessingPayment}
              paymentStatus={props.paymentStatus}
              isSuccess={props.isSuccess}
              managedPaymentDetails={props.managedPaymentDetails}
              tokenDelegatePaymentDetails={props.tokenDelegatePaymentDetails}
              discount={props.discount}
              onCheckout={(params) => {
                const freeInput = lockFreeInputSelection()
                onCheckout({
                  ...params,
                  freeInput
                })
              }}
              onInputFieldsChange={props.onInputFieldsChange}
              onQuoteUpdate={() => getPriceQuotes()}
              successElement={props.successElement}
              errorMessage={props.errorMessage}
              travelRuleFieldsCollected={props.travelRuleFieldsCollected}
              hideTravelRuleFields={props.hideTravelRuleFields}
              onTravelRuleFieldsContinue={lockFreeInputSelection}
              onCheckoutStateChange={(isCheckoutState) => {
                setHideMobileSummary(
                  !!checkoutData.gateway.managed &&
                  isCheckoutState
                )
              }}
              onChangePaymentMethod={props.onChangePaymentMethod}
            />
          </PayWidgetFitForPhone>
        </RightFormContainer>
        {hideMobileSummary && (
          <MobileRightFooter style={{ color: leftFooterTextColor }}>
            <LeftFooterInline
              $columnWidth={leftRailColumnWidth}
              $columnWidthNarrow={leftRailColumnWidthNarrow}
              $columnWidthWide={leftRailColumnWidthWide}
            >
              <a
                href="https://radom.com"
                target="_blank"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 6,
                  cursor: 'pointer',
                  userSelect: 'none',
                  color: 'inherit',
                  fontWeight: 400,
                  textDecoration: 'none'
                }}
                rel="noreferrer"
              >
                <span>Powered by</span>
                <RadomLogo
                  width={50}
                  fill={leftFooterTextColor}
                />
              </a>
              <span style={{ opacity: 0.5 }}>|</span>
              <a
                href="https://www.radom.com/terms"
                target="_blank"
                rel="noreferrer"
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                Terms
              </a>
              <span style={{ opacity: 0.5 }}>|</span>
              <a
                href="https://radom.com/privacy"
                target="_blank"
                rel="noreferrer"
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                Privacy
              </a>
            </LeftFooterInline>
          </MobileRightFooter>
        )}
      </RightSection>
    </FadeInWrapper>
  )
}

export default Checkout
